// 右侧侧滑抽屉：用于批量复制、复杂表单。
// portal 到 body；ESC + 点遮罩关闭。

import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";

export interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  description?: ReactNode;
  side?: "right" | "left";
  children: ReactNode;
  className?: string;
}

export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  side = "right",
  children,
  className,
}: SheetProps) {
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onOpenChange]);

  if (!open) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === "string" ? title : undefined}
      className="fixed inset-0 z-50 bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      <aside
        data-side={side}
        className={cn(
          "absolute top-0 flex h-full w-[min(560px,92vw)] flex-col overflow-y-auto border-l border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 shadow-lg",
          side === "right" ? "right-0" : "left-0 border-l-0 border-r",
          className,
        )}
      >
        {title ? (
          <h2 className="mb-1 text-base font-semibold">{title}</h2>
        ) : null}
        {description ? (
          <p className="mb-3 text-xs text-[hsl(var(--muted-foreground))]">
            {description}
          </p>
        ) : null}
        {children}
      </aside>
    </div>,
    document.body,
  );
}
